/**
 * Scheletul aplicației autenticate: navigație laterală, bară superioară, comutator de cont.
 *
 * Responsive prin construcție (cerința #36): pe desktop sidebar-ul este fix, pe tabletă se
 * strânge la iconițe, iar pe mobil devine un drawer cu fundal întunecat. Nimic nu iese din ecran.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Badge, Button, Icon, StatusDot, cx, type IconName } from './ui.js';
import { api } from '../lib/api.js';
import { useSession } from '../lib/session.js';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  permission?: string;
  /** Prezent doar dacă planul contului include funcția. */
  feature?: string;
  end?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

/** Consola de platformă. Vizibilă doar pentru administratorul Menuvia, în același sidebar. */
const PLATFORM_NAV: NavItem[] = [
  { to: '/admin', label: 'Privire generală', icon: 'chart', end: true },
  { to: '/admin/accounts', label: 'Conturi', icon: 'store' },
  { to: '/admin/restaurants', label: 'Toate restaurantele', icon: 'grid' },
  { to: '/admin/users', label: 'Utilizatori', icon: 'users' },
  { to: '/admin/plans', label: 'Planuri', icon: 'tag' },
  { to: '/admin/subscriptions', label: 'Abonamente', icon: 'card' },
  { to: '/admin/invoices', label: 'Facturi', icon: 'chart' },
  { to: '/admin/flags', label: 'Feature flags', icon: 'sparkles' },
  { to: '/admin/audit', label: 'Audit', icon: 'shield' },
  { to: '/admin/system', label: 'Sistem', icon: 'settings' },
];

const NAV: NavGroup[] = [
  {
    label: 'Operare',
    items: [
      { to: '/app', label: 'Astăzi', icon: 'home' },
      { to: '/app/orders', label: 'Comenzi', icon: 'receipt', permission: 'order.view' },
      { to: '/app/menus', label: 'Meniuri', icon: 'menu', permission: 'menu.view' },
      { to: '/app/products', label: 'Produse', icon: 'tag', permission: 'product.view' },
      { to: '/app/qr', label: 'Coduri QR', icon: 'qr', permission: 'qr.view' },
      { to: '/app/displays', label: 'Ecrane', icon: 'display', permission: 'display.view' },
      { to: '/app/playlists', label: 'Liste de redare', icon: 'image', permission: 'display.view' },
    ],
  },
  {
    label: 'Analiză',
    items: [
      { to: '/app/analytics', label: 'Statistici', icon: 'chart', permission: 'analytics.view' },
      { to: '/app/promotions', label: 'Promoții', icon: 'sparkles', permission: 'promotion.view' },
    ],
  },
  {
    label: 'Cont',
    items: [
      { to: '/app/restaurants', label: 'Restaurante', icon: 'store' },
      { to: '/app/domain', label: 'Domeniu', icon: 'globe', permission: 'restaurant.update', feature: 'custom_domain' },
      { to: '/app/locations', label: 'Locații', icon: 'pin', permission: 'location.update' },
      { to: '/app/team', label: 'Echipă', icon: 'users', permission: 'team.view' },
      { to: '/app/billing', label: 'Abonament', icon: 'card', permission: 'billing.view' },
      { to: '/app/settings', label: 'Setări', icon: 'settings' },
    ],
  },
];

interface PlatformAccount {
  id: string;
  name: string;
  slug: string;
  status: string;
}

export function AppShell() {
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [platformAccounts, setPlatformAccounts] = useState<PlatformAccount[]>([]);

  useEffect(() => {
    if (!session.user?.isPlatformAdmin) {
      setPlatformAccounts([]);
      return;
    }
    let cancelled = false;
    api
      .get<PlatformAccount[]>('/admin/accounts?limit=100')
      .then((rows) => {
        if (!cancelled) setPlatformAccounts(rows);
      })
      .catch(() => {
        if (!cancelled) setPlatformAccounts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [session.user?.isPlatformAdmin]);

  // Închidem meniurile la schimbarea rutei — fără „meniuri lipicioase".
  useEffect(() => {
    setMobileOpen(false);
    setAccountMenuOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  const activeAccount = session.accounts.find((a) => a.id === session.activeAccountId) ?? session.accounts[0];
  const supportAccount =
    session.user?.isPlatformAdmin && session.activeAccountId
      ? (platformAccounts.find((account) => account.id === session.activeAccountId) ??
        (activeAccount?.id === session.activeAccountId ? activeAccount : null))
      : null;

  const navigation = (
    <nav aria-label="Navigație principală" className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-3 py-4">
      {session.user?.isPlatformAdmin ? (
        <div className="flex flex-col gap-1">
          <p className="px-3 pb-1 text-micro font-medium tracking-wide text-ink-400 uppercase">Platformă</p>
          {PLATFORM_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cx(
                  'transition-ui group flex items-center gap-3 rounded-[var(--radius-md)] px-3 py-2.5 text-body-s',
                  isActive
                    ? 'bg-brand-100/70 font-medium text-brand-700'
                    : 'text-ink-600 hover:bg-surface-3 hover:text-ink-950',
                )
              }
            >
              <Icon name={item.icon} />
              <span className="flex-1 truncate">{item.label}</span>
            </NavLink>
          ))}
        </div>
      ) : null}
      {NAV.map((group) => {
        const items = group.items.filter(
          (item) =>
            (!item.permission || session.can(item.permission)) &&
            (!item.feature || session.features.includes(item.feature)),
        );
        if (!items.length) return null;
        return (
          <div key={group.label} className="flex flex-col gap-1">
            <p className="px-3 pb-1 text-micro font-medium tracking-wide text-ink-400 uppercase">{group.label}</p>
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/app'}
                className={({ isActive }) =>
                  cx(
                    'transition-ui group flex items-center gap-3 rounded-[var(--radius-md)] px-3 py-2.5 text-body-s',
                    isActive
                      ? 'bg-brand-100/70 font-medium text-brand-700'
                      : 'text-ink-600 hover:bg-surface-3 hover:text-ink-950',
                  )
                }
              >
                <Icon name={item.icon} />
                <span className="flex-1 truncate">{item.label}</span>
              </NavLink>
            ))}
          </div>
        );
      })}
    </nav>
  );

  return (
    <div className="flex min-h-dvh bg-surface-2">
      {/* Sidebar desktop */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-ink-100 bg-surface lg:flex">
        <Brand />
        {navigation}
        <AccountSwitcher
          open={accountMenuOpen}
          platformAccounts={platformAccounts}
          onToggle={() => setAccountMenuOpen((v) => !v)}
          onSelect={async (id) => {
            await session.switchAccount(id);
            navigate('/app');
          }}
          onLeave={async () => {
            await session.leaveAccount();
            navigate('/app');
          }}
        />
      </aside>

      {/* Drawer mobil */}
      {mobileOpen ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            aria-label="Închide meniul"
            className="absolute inset-0 bg-ink-950/45"
            onClick={() => setMobileOpen(false)}
          />
          <div className="page-enter relative flex h-full min-h-0 w-[280px] flex-col border-r border-ink-100 bg-surface">
            <Brand onClose={() => setMobileOpen(false)} />
            {navigation}
            <AccountSwitcher
              open
              platformAccounts={platformAccounts}
              onToggle={() => undefined}
              onSelect={async (id) => {
                await session.switchAccount(id);
                navigate('/app');
              }}
              onLeave={async () => {
                await session.leaveAccount();
                navigate('/app');
              }}
            />
          </div>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-ink-100 bg-surface/95 px-4 backdrop-blur sm:px-6">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label="Deschide meniul"
            className="transition-ui rounded-[var(--radius-md)] p-2 text-ink-600 hover:bg-surface-3 lg:hidden"
          >
            <Icon name="menu" size={20} />
          </button>

          <div className="hidden min-w-0 flex-1 items-center gap-2 sm:flex">
            <StatusDot tone="success" pulse />
            <span className="truncate text-body-s text-ink-600">
              {supportAccount?.name ?? (activeAccount ? activeAccount.name : 'Cont fără context')} · rol {session.role ?? '—'}
            </span>
          </div>

          <div className="flex flex-1 items-center justify-end gap-1.5 sm:flex-none">
            <NavLink
              to="/app/notifications"
              aria-label="Notificări"
              className="transition-ui relative rounded-[var(--radius-md)] p-2 text-ink-600 hover:bg-surface-3 hover:text-ink-950"
            >
              <Icon name="bell" size={19} />
              {session.unreadNotifications > 0 ? (
                <span className="absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 text-micro font-medium text-white">
                  {session.unreadNotifications > 9 ? '9+' : session.unreadNotifications}
                </span>
              ) : null}
            </NavLink>

            <div className="relative">
              <button
                type="button"
                onClick={() => setUserMenuOpen((v) => !v)}
                aria-expanded={userMenuOpen}
                aria-haspopup="menu"
                className="transition-ui flex items-center gap-2 rounded-full p-1 pr-2 hover:bg-surface-3"
              >
                <Avatar name={session.user?.name ?? '?'} size={32} />
                <Icon name="chevron-down" size={16} className="text-ink-400" />
              </button>
              {userMenuOpen ? (
                <div
                  role="menu"
                  className="page-enter absolute right-0 mt-2 w-64 rounded-[var(--radius-md)] border border-ink-100 bg-surface p-2 shadow-[var(--shadow-elevated)]"
                >
                  <div className="flex items-center gap-3 rounded-[var(--radius-sm)] px-2 py-2">
                    <Avatar name={session.user?.name ?? '?'} size={36} />
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-body-s font-medium text-ink-950">{session.user?.name}</span>
                      <span className="truncate text-caption text-ink-400">{session.user?.email}</span>
                    </div>
                  </div>
                  {!session.user?.emailVerified ? (
                    <div className="mx-2 mb-1">
                      <Badge tone="warning">Email neconfirmat</Badge>
                    </div>
                  ) : null}
                  <hr className="my-2 border-ink-100" />
                  <MenuLink to="/app/settings" icon="settings" label="Setări cont" />
                  <MenuLink to="/app/team" icon="shield" label="Securitate echipă" />
                  {session.user?.isPlatformAdmin ? (
                    <MenuLink to="/admin" icon="sparkles" label="Consola platformei" />
                  ) : null}
                  <MenuLink to="/" icon="external" label="Pagina publică" />
                  <button
                    type="button"
                    role="menuitem"
                    onClick={async () => {
                      await session.logout();
                      navigate('/login');
                    }}
                    className="transition-ui flex w-full items-center gap-3 rounded-[var(--radius-sm)] px-2 py-2 text-left text-body-s text-ink-600 hover:bg-surface-3 hover:text-ink-950"
                  >
                    <Icon name="logout" size={17} />
                    Deconectare
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>

        {supportAccount ? (
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-body-s text-amber-950 sm:px-6">
            <span>
              Lucrezi în contul <span className="font-medium">{supportAccount.name}</span> ca administrator de platformă.
              Meniurile, ecranele și restul sunt ale acestui restaurant.
            </span>
            <button
              type="button"
              className="font-medium underline"
              onClick={() => {
                void session.leaveAccount().then(() => navigate('/app'));
              }}
            >
              Ieși din cont
            </button>
          </div>
        ) : null}

        <main className="mx-auto w-full max-w-[1440px] flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function MenuLink({ to, icon, label }: { to: string; icon: IconName; label: string }) {
  return (
    <NavLink
      to={to}
      role="menuitem"
      className="transition-ui flex items-center gap-3 rounded-[var(--radius-sm)] px-2 py-2 text-body-s text-ink-600 hover:bg-surface-3 hover:text-ink-950"
    >
      <Icon name={icon} size={17} />
      {label}
    </NavLink>
  );
}

function Brand({ onClose }: { onClose?: () => void }) {
  return (
    <div className="flex h-16 shrink-0 items-center justify-between border-b border-ink-100 px-5">
      <NavLink to="/app" className="flex items-center gap-2.5">
        <span className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)] bg-brand-600 text-white">
          <Icon name="qr" size={17} />
        </span>
        <span className="font-display text-title text-ink-950">Menuvia</span>
      </NavLink>
      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          aria-label="Închide meniul"
          className="transition-ui rounded p-1.5 text-ink-400 hover:bg-surface-3 lg:hidden"
        >
          <Icon name="close" size={18} />
        </button>
      ) : null}
    </div>
  );
}

function AccountSwitcher({
  open,
  platformAccounts,
  onToggle,
  onSelect,
  onLeave,
}: {
  open: boolean;
  platformAccounts: Array<{ id: string; name: string; status: string }>;
  onToggle: () => void;
  onSelect: (accountId: string) => Promise<void>;
  onLeave: () => Promise<void>;
}) {
  const session = useSession();
  const membership =
    session.accounts.find((a) => a.id === session.activeAccountId) ??
    (session.user?.isPlatformAdmin ? undefined : session.accounts[0]);
  const entered = platformAccounts.find((account) => account.id === session.activeAccountId);
  const activeName = membership?.name ?? entered?.name;
  const activeRole = membership?.role ?? (entered ? 'administrator' : undefined);
  const choices = session.user?.isPlatformAdmin
    ? platformAccounts.map((account) => ({
        id: account.id,
        name: account.name,
        role: session.accounts.find((item) => item.id === account.id)?.role ?? account.status,
      }))
    : session.accounts.map((account) => ({ id: account.id, name: account.name, role: account.role }));
  const showList = open && (session.user?.isPlatformAdmin ? choices.length > 0 : choices.length > 1);

  return (
    <div className="relative m-3 shrink-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="transition-ui flex w-full items-center gap-3 rounded-[var(--radius-md)] border border-ink-100 bg-surface px-3 py-2.5 text-left hover:bg-surface-3"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)] bg-surface-3 text-ink-600">
          <Icon name="store" size={16} />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-body-s font-medium text-ink-950">{activeName ?? 'Fără cont'}</span>
          <span className="truncate text-caption text-ink-400">{activeRole ?? 'selectează un cont'}</span>
        </span>
        <Icon name="chevron-down" size={16} className={cx('text-ink-400 transition-ui', open && 'rotate-180')} />
      </button>

      {showList ? (
        <ul
          role="listbox"
          className="page-enter absolute bottom-full left-0 z-20 mb-2 max-h-72 w-full overflow-y-auto rounded-[var(--radius-md)] border border-ink-100 bg-surface p-1.5 shadow-[var(--shadow-elevated)]"
        >
          {choices.map((account) => (
            <li key={account.id}>
              <button
                type="button"
                role="option"
                aria-selected={account.id === session.activeAccountId}
                onClick={() => void onSelect(account.id)}
                className={cx(
                  'transition-ui flex w-full items-center justify-between gap-2 rounded-[var(--radius-sm)] px-3 py-2 text-left text-body-s',
                  account.id === session.activeAccountId ? 'bg-brand-100/70 text-brand-700' : 'text-ink-600 hover:bg-surface-3',
                )}
              >
                <span className="truncate">{account.name}</span>
                <span className="text-caption text-ink-400">{account.role}</span>
              </button>
            </li>
          ))}
          {session.user?.isPlatformAdmin && session.activeAccountId ? (
            <li>
              <button
                type="button"
                onClick={() => void onLeave()}
                className="transition-ui mt-1 w-full rounded-[var(--radius-sm)] px-3 py-2 text-left text-body-s text-ink-600 hover:bg-surface-3"
              >
                Ieși din cont
              </button>
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}

/** Ecran de așteptare la încărcarea sesiunii (fără „flash" de conținut greșit). */
export function FullPageLoader({ label = 'Se încarcă…' }: { label?: string }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-surface-2">
      <div className="flex flex-col items-center gap-3 text-ink-400">
        <span className="flex h-10 w-10 items-center justify-center rounded-[var(--radius-md)] bg-brand-600 text-white">
          <Icon name="qr" size={20} />
        </span>
        <p className="text-body-s">{label}</p>
      </div>
    </div>
  );
}

/** Înveliș pentru paginile publice (login, înregistrare, invitație). */
export function AuthLayout({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="grid min-h-dvh lg:grid-cols-[1fr_minmax(0,520px)]">
      <div className="flex items-center justify-center px-5 py-10 sm:px-8">{children}</div>
      <aside className="relative hidden flex-col justify-between overflow-hidden bg-brand-700 p-10 text-white lg:flex">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.16]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, #ffffff 0.5px, transparent 0.5px), radial-gradient(circle at 70% 60%, #ffffff 0.5px, transparent 0.5px)",
            backgroundSize: '28px 28px, 36px 36px',
          }}
        />
        <div className="relative flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-[var(--radius-sm)] bg-white/15">
            <Icon name="qr" size={18} />
          </span>
          <span className="font-display text-title">Menuvia</span>
        </div>
        <div className="relative flex flex-col gap-5">
          {aside ?? (
            <>
              <h2 className="font-display text-display-m leading-tight">
                Un singur meniu. Toate ecranele restaurantului tău.
              </h2>
              <ul className="flex flex-col gap-3 text-body-s text-white/85">
                {[
                  'Modifici prețul o dată — se actualizează pe QR, pe site și pe ecranele din sală.',
                  'QR-urile rămân valabile la infinit: schimbi meniul, nu afișul.',
                  'Ecranele continuă să afișeze meniul chiar și când cade internetul.',
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3">
                    <Icon name="check" size={18} className="mt-0.5 shrink-0 text-white" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
        <p className="relative text-caption text-white/60">
          Datele fiecărui restaurant sunt izolate complet, la nivel de bază de date.
        </p>
      </aside>
    </div>
  );
}

/** Bară simplă pentru stări de eroare/întârziere la nivel de pagină. */
export function PageState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 py-16 text-center">
      <h2 className="font-display text-title text-ink-950">{title}</h2>
      {description ? <p className="max-w-md text-body-s text-ink-600">{description}</p> : null}
      {action}
    </div>
  );
}

export function PrimaryAction({ to, label, icon = 'plus' }: { to: string; label: string; icon?: IconName }) {
  return (
    <Button onClick={() => (window.location.href = to)} icon={<Icon name={icon} size={17} />}>
      {label}
    </Button>
  );
}
